import React, { FC, Dispatch, SetStateAction, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { FunCompWithChildren } from '../../App'
import useShouldAnimate from '../../hooks/useShouldAnimate'

const Fade = styled.div<{
  shouldAnimate: boolean
  delay: number
  duration: number
  zIndex: number
  disabled: boolean
}>`
  z-index: ${({ zIndex }) => zIndex};
  opacity: ${({ shouldAnimate }) => (shouldAnimate ? 1 : 0)};
  transition: ${({ duration }) => duration}s ease-in-out;
  transition-delay: ${({ delay }) => delay}s;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0;
    `}
`

interface Props {
  runMultiple?: boolean
  delay?: number
  duration?: number
  zIndex?: number
  animationCompleteSetter?: Dispatch<SetStateAction<boolean>>
  animationCompleteTrigger?: boolean
  awaitAnimation?: boolean
  awaitingComplete?: boolean
  className?: string
  fullWidth?: boolean
}

const FadeIn: FunCompWithChildren<Props> = ({
  children,
  runMultiple = process.env.NODE_ENV !== 'production',
  delay = 0.3,
  duration = 0.3,
  zIndex = 1,
  animationCompleteSetter,
  animationCompleteTrigger = false,
  awaitAnimation = false,
  awaitingComplete = false,
  className = '',
  fullWidth = false,
}) => {
  const [disabled, setDisabled] = useState(awaitAnimation)
  const { ref, shouldAnimate } = useShouldAnimate(runMultiple)

  useEffect(() => {
    if (!awaitAnimation) return
    setDisabled(!awaitingComplete)
  }, [awaitAnimation, awaitingComplete])

  useEffect(() => {
    if (!animationCompleteSetter) return
    const timeout = setTimeout(() => {
      if (animationCompleteTrigger) return
      animationCompleteSetter(shouldAnimate)
    }, (delay + duration) * 1000)
    return () => clearTimeout(timeout)
  }, [
    shouldAnimate,
    animationCompleteSetter,
    delay,
    duration,
    animationCompleteTrigger,
  ])

  return (
    <Fade
      className={className}
      ref={ref}
      shouldAnimate={shouldAnimate}
      delay={delay}
      duration={duration}
      zIndex={zIndex}
      disabled={disabled}
      style={{ width: fullWidth ? '100%' : 'auto' }}
    >
      {children}
    </Fade>
  )
}

export default FadeIn
